import React, { useState, useEffect, useCallback } from 'react';
import { useApp } from '../contexts/AppContext';
import Sidebar from '../components/Sidebar';
import CreditReportViewer from '../components/CreditReportViewer';
import { Plus, RefreshCw, CreditCard, ArrowRightLeft, AlertCircle, X } from 'lucide-react';

const StatCard = ({ title, value, sub, color = 'primary', blink }) => {
  const colors = { primary: 'text-primary', emerald: 'text-emerald-500', amber: 'text-amber-500', red: 'text-destructive' };
  return (
    <div className={`stat-card animate-fade-in-up ${blink === 'success' ? 'blink-success' : blink === 'failure' ? 'blink-failure' : ''}`} data-accent={color}>
      <p className="text-xs text-muted-foreground font-medium mb-2">{title}</p>
      <p className={`text-3xl font-extrabold ${colors[color]}`}>{value ?? '—'}</p>
      {sub && <p className="text-xs text-muted-foreground mt-2">{sub}</p>}
    </div>
  );
};

const Badge = ({ status }) => {
  if (status === 'active') return <span className="badge-active">Active</span>;
  if (status === 'suspended') return <span className="badge-suspended">Suspended</span>;
  return <span className="inline-flex px-2 py-0.5 rounded-full text-xs bg-secondary text-muted-foreground">{status}</span>;
};

export default function AdminDashboard() {
  const { t, apiCall, user } = useApp();
  const [activeTab, setActiveTab] = useState('overview');

  // Log tab switch when admin navigates away from KYC & Reports mid-flow
  const handleSetActiveTab = (newTab) => {
    if (activeTab === 'credit-report' && newTab !== 'credit-report') {
      apiCall('POST', '/reports/log-event', {
        event_type: 'tab_switch',
        button_name: '',
        step: 'in-credit-report-flow',
        tab_switched_to: newTab,
        subject_mobile: '',
        local_time: new Date().toLocaleString(),
      }).catch(() => {});
    }
    setActiveTab(newTab);
  };
  const [reportStatus, setReportStatus] = useState(null); // 'success' | 'failure' | null

  const handleReportResult = (status) => {
    setReportStatus(status);
    setTimeout(() => setReportStatus(null), 4000);
  };
  const [stats, setStats] = useState({});
  const [ngos, setNgos] = useState([]);
  const [wallet, setWallet] = useState({ balance: 0, ledger: [] });
  const [purchases, setPurchases] = useState([]);
  const [usageLogs, setUsageLogs] = useState([]);
  const [naReportsData, setNaReportsData] = useState({ reports: [], monthly_count: 0, claimed_so_far: 0, claims_used: 0, can_claim_more: false, max_claimable_now: 0 });
  const [claimModal, setClaimModal] = useState(null);
  const [claimAmount, setClaimAmount] = useState(1);
  const [logoSettings, setLogoSettings] = useState({});
  const [settings, setSettings] = useState({});
  const [toast, setToast] = useState('');
  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(''), 3000); };

  const loadPublicSettings = useCallback(async () => {
    try {
      const [pub, ovw] = await Promise.all([
        apiCall('GET', '/settings/public'),
        apiCall('GET', '/admin/overview')
      ]);
      setLogoSettings(pub); setSettings(pub); setStats(ovw);
    } catch {}
  }, [apiCall]);

  useEffect(() => { loadPublicSettings(); }, [loadPublicSettings]);

  useEffect(() => {
    const loaders = {
      ngos: () => apiCall('GET', '/admin/ngos').then(r => setNgos(r.ngos)),
      purchase: () => apiCall('GET', '/admin/wallet').then(setWallet),
      'purchase-history': () => apiCall('GET', '/admin/purchase-history').then(r => setPurchases(r.purchases)),
      'usage-logs': () => apiCall('GET', '/admin/usage-logs').then(r => setUsageLogs(r.logs)),
      'na-reports': () => apiCall('GET', '/admin/na-reports').then(setNaReportsData),
      overview: () => apiCall('GET', '/admin/overview').then(setStats).catch(() => {}),
    };
    if (loaders[activeTab]) loaders[activeTab]().catch(() => {});
  }, [activeTab, apiCall]);

  // Real-time overview polling (30s)
  useEffect(() => {
    if (activeTab !== 'overview') return;
    const timer = setInterval(() => {
      apiCall('GET', '/admin/overview').then(setStats).catch(() => {});
    }, 30000);
    return () => clearInterval(timer);
  }, [activeTab, apiCall]);

  // ── Overview ──
  const renderOverview = () => (
    <div>
      <div className="mb-8">
        <h1 className="text-2xl lg:text-3xl font-bold text-foreground">
          Welcome back, {user?.name || 'Admin'}
        </h1>
        <p className="text-sm text-muted-foreground mt-1">Here's your admin overview for today</p>
      </div>
      <div data-testid="credit-balance-badge" className="inline-flex items-center gap-2 px-3 py-1.5 bg-primary/8 border border-primary/20 rounded-xl mb-6">
        <CreditCard size={13} className="text-primary" />
        <span className="text-sm font-bold text-primary">{stats.admin_wallet_balance ?? 0} Credits</span>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        <StatCard title={t('walletBalance')} value={stats.admin_wallet_balance} sub="credits" />
        <StatCard title={t('totalNGOs')} value={stats.ngo_count} color="emerald" />
        <StatCard title={t('ngoCredits')} value={stats.total_ngo_credits} color="amber" />
        <StatCard title={t('totalReports')} value={stats.total_reports_pulled} blink={reportStatus} />
        <StatCard title={t('totalDownloads')} value={stats.total_reports_downloaded} />
        <StatCard title="Total Purchases" value={stats.total_purchases} color="emerald" />
      </div>
    </div>
  );

  // ── Purchase Credits ──
  const [orderLoading, setOrderLoading] = useState(false);
  const handlePurchase = async (bundleType) => {
    setOrderLoading(true);
    try {
      const order = await apiCall('POST', '/razorpay/create-order', { bundle_type: bundleType });
      if (!order.order_id) { showToast('Razorpay not configured. Please contact developer.'); setOrderLoading(false); return; }
      const options = {
        key: order.key_id,
        amount: order.amount,
        currency: order.currency,
        order_id: order.order_id,
        name: 'Credit Platform',
        description: `${order.credits} Credits`,
        handler: async (response) => {
          try {
            const res = await apiCall('POST', '/razorpay/verify-payment', {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              bundle_type: bundleType
            });
            showToast(`✓ Payment successful! ${res.credits_added} credits added.`);
            loadPublicSettings();
          } catch { showToast('Payment verification failed. Contact support.'); }
        },
        prefill: { name: user?.name || 'Admin' },
        theme: { color: '#0284C7' }
      };
      if (window.Razorpay) new window.Razorpay(options).open();
      else showToast('Razorpay not loaded. Please refresh and try again.');
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to create order');
    } finally { setOrderLoading(false); }
  };

  // Load Razorpay script
  useEffect(() => {
    if (!document.getElementById('razorpay-script')) {
      const script = document.createElement('script');
      script.id = 'razorpay-script';
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      document.head.appendChild(script);
    }
  }, []);

  const renderPurchase = () => (
    <div>
      <h2 className="text-xl font-bold mb-2">{t('purchaseCredits')}</h2>
      <p className="text-sm text-muted-foreground mb-6">Select a bundle to purchase credits for your wallet.</p>
      {!settings.razorpay_configured && (
        <div className="mb-4 px-4 py-3 bg-amber-500/10 border border-amber-500/20 rounded-xl text-sm text-amber-600 dark:text-amber-400">
          Razorpay is not configured. Contact the developer to enable payments.
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-2xl">
        {[
          { type: 1, credits: settings.bundle_1_credits || 350, price: settings.bundle_1_price || 9100, label: t('bundle1') },
          { type: 2, credits: settings.bundle_2_credits || 700, price: settings.bundle_2_price || 18200, label: t('bundle2') },
        ].map(bundle => (
          <div key={bundle.type} className="bg-card border-2 border-border hover:border-primary rounded-2xl p-6 transition-all">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-lg">{bundle.label}</h3>
              {bundle.type === 2 && <span className="px-2 py-0.5 bg-primary/10 text-primary text-xs rounded-full font-medium">Popular</span>}
            </div>
            <p className="text-4xl font-extrabold text-foreground mb-1">
              ₹{bundle.price?.toLocaleString()}
            </p>
            <p className="text-muted-foreground text-sm mb-4">
              {bundle.credits} {t('bundleCredits')}
              <span className="ml-2 text-xs bg-secondary px-2 py-0.5 rounded-full">
                ₹{Math.round((bundle.price / bundle.credits) * 100) / 100}/{t('perCredit')}
              </span>
            </p>
            <button data-testid={`buy-bundle-${bundle.type}`} onClick={() => handlePurchase(bundle.type)}
              disabled={orderLoading || !settings.razorpay_configured}
              className="btn-primary w-full py-3">
              {orderLoading ? '...' : t('buyNow')}
            </button>
          </div>
        ))}
      </div>
      <div className="mt-8 bg-card border border-border rounded-xl p-4">
        <h3 className="font-semibold text-sm mb-3">Wallet Balance</h3>
        <p className="text-2xl font-bold text-primary">{stats.admin_wallet_balance || 0} credits</p>
        {wallet.ledger?.length > 0 && (
          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-xs"><thead><tr className="border-b border-border">
              {['Type', 'Description', 'Amount', 'Balance After', 'Date'].map(h => <th key={h} className="text-left py-2 px-2 text-muted-foreground font-medium">{h}</th>)}
            </tr></thead><tbody>
              {wallet.ledger.slice(0, 10).map((e, i) => (
                <tr key={i} className="border-b border-border/50">
                  <td className="py-2 px-2">
                    <span className={`px-1.5 py-0.5 rounded text-xs ${e.transaction_type === 'credit' ? 'bg-emerald-500/10 text-emerald-600' : 'bg-red-500/10 text-red-600'}`}>
                      {e.transaction_type}
                    </span>
                  </td>
                  <td className="py-2 px-2 max-w-xs truncate">{(e.description||'').replace(/\bdeveloper\b/gi, 'API Provider')}</td>
                  <td className="py-2 px-2 font-medium">{e.amount}</td>
                  <td className="py-2 px-2 font-bold">{e.balance_after}</td>
                  <td className="py-2 px-2 text-muted-foreground">{new Date(e.created_at).toLocaleString()}</td>
                </tr>
              ))}
            </tbody></table>
          </div>
        )}
      </div>
    </div>
  );

  // ── NGO Users ──
  const [newNgo, setNewNgo] = useState({ name: '', mobile_no: '', email: '' });
  const [assignNgo, setAssignNgo] = useState({ ngo_id: '', credits: '', reassign_to: '' });

  const renderNGOs = () => (
    <div className="space-y-6">
      <div className="bg-card border border-border rounded-xl p-5">
        <h3 className="font-semibold text-sm mb-4">{t('createNgo')}</h3>
        <form className="flex gap-3 flex-wrap" onSubmit={async e => {
          e.preventDefault();
          try { await apiCall('POST', '/admin/ngos', newNgo); showToast('NGO user created!'); setNewNgo({ name: '', mobile_no: '', email: '' }); apiCall('GET', '/admin/ngos').then(r => setNgos(r.ngos)); }
          catch (err) { showToast(err.response?.data?.detail || 'Error'); }
        }}>
          <input data-testid="ngo-name-input" className="form-input flex-1 min-w-48" placeholder={t('name')} required
            value={newNgo.name} onChange={e => setNewNgo(p => ({ ...p, name: e.target.value }))} />
          <input data-testid="ngo-mobile-input" className="form-input flex-1 min-w-48" placeholder={t('mobile')} required type="tel"
            value={newNgo.mobile_no} onChange={e => setNewNgo(p => ({ ...p, mobile_no: e.target.value }))} />
          <input className="form-input flex-1 min-w-48" placeholder="Email Address" type="email" required
            value={newNgo.email} onChange={e => setNewNgo(p => ({ ...p, email: e.target.value }))} />
          <button data-testid="create-ngo-btn" type="submit" className="btn-primary flex items-center gap-1.5"><Plus size={14} />{t('create')}</button>
        </form>
      </div>
      <div className="bg-card border border-border rounded-xl p-5">
        <h3 className="font-semibold text-sm mb-4">{t('assignToNgo')}</h3>
        <form className="flex gap-3 flex-wrap" onSubmit={async e => {
          e.preventDefault();
          try { await apiCall('POST', '/admin/credits/assign-to-ngo', { ngo_id: assignNgo.ngo_id, credits: parseInt(assignNgo.credits) });
            showToast('Credits assigned!'); apiCall('GET', '/admin/ngos').then(r => setNgos(r.ngos)); setAssignNgo(p => ({ ...p, credits: '' })); }
          catch (err) { showToast(err.response?.data?.detail || 'Error'); }
        }}>
          <select data-testid="assign-ngo-select" className="form-input flex-1 min-w-48"
            value={assignNgo.ngo_id} onChange={e => setAssignNgo(p => ({ ...p, ngo_id: e.target.value }))} required>
            <option value="">Select NGO</option>
            {ngos.map(n => <option key={n.id} value={n.id}>{n.name} ({n.credit_balance} credits)</option>)}
          </select>
          <input data-testid="ngo-assign-credits-input" className="form-input w-32" type="number" min="1" placeholder="Credits" required
            value={assignNgo.credits} onChange={e => setAssignNgo(p => ({ ...p, credits: e.target.value }))} />
          <button type="submit" className="btn-primary">{t('assign')}</button>
        </form>
      </div>
      <div className="overflow-x-auto rounded-xl border border-border">
        <table className="w-full text-sm"><thead><tr className="border-b border-border bg-secondary/30">
          {[t('userName'), t('userMobile'), t('userBalance'), t('userReports'), t('userStatus'), t('actions')].map(h => (
            <th key={h} className="text-left px-4 py-3 text-xs font-medium text-muted-foreground">{h}</th>
          ))}
        </tr></thead><tbody>
          {ngos.length === 0 ? <tr><td colSpan={6} className="text-center py-8 text-muted-foreground text-sm">{t('noUsers')}</td></tr> :
            ngos.map(n => (
              <tr key={n.id} className="border-b border-border/50 hover:bg-secondary/20">
                <td className="px-4 py-3 font-medium">{n.name}</td>
                <td className="px-4 py-3 font-mono-code text-xs">{n.mobile_no}</td>
                <td className="px-4 py-3 font-bold text-primary">{n.credit_balance}</td>
                <td className="px-4 py-3">{n.reports_pulled}</td>
                <td className="px-4 py-3"><Badge status={n.status} /></td>
                <td className="px-4 py-3">
                  <button onClick={() => setAssignNgo(p => ({ ...p, ngo_id: n.id }))}
                    className="text-xs px-2 py-1 rounded text-primary hover:bg-primary/10">
                    {t('assign')}
                  </button>
                </td>
              </tr>
            ))}
        </tbody></table>
      </div>
    </div>
  );

  // ── Usage Logs ──
  const renderUsageLogs = () => (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-sm">{t('usageLogs')}</h3>
        <button onClick={() => apiCall('GET', '/admin/usage-logs').then(r => setUsageLogs(r.logs))}
          className="btn-secondary text-xs flex items-center gap-1.5"><RefreshCw size={12} />{t('refresh')}</button>
      </div>
      <div className="overflow-x-auto rounded-xl border border-border">
        <table className="w-full text-xs"><thead><tr className="border-b border-border bg-secondary/30">
          {['Subject', 'Mobile', 'Pulled By', 'IP', 'Mock', t('timestamp')].map(h => (
            <th key={h} className="text-left px-3 py-2 text-muted-foreground font-medium">{h}</th>
          ))}
        </tr></thead><tbody>
          {usageLogs.length === 0 ? <tr><td colSpan={6} className="text-center py-8 text-muted-foreground">{t('noLogs')}</td></tr> :
            usageLogs.filter(log => log.created_by_role !== 'developer').map(log => (
              <tr key={log.id} className="border-b border-border/50 hover:bg-secondary/20">
                <td className="px-3 py-2 font-medium">{log.subject_name}</td>
                <td className="px-3 py-2 font-mono-code">{log.subject_mobile}</td>
                <td className="px-3 py-2">
                  {log.created_by_role === 'ngo'
                    ? <span>NGO User: <strong>{log.created_by_name || 'NGO'}</strong></span>
                    : <span className="text-primary font-medium">Admin</span>
                  }
                </td>
                <td className="px-3 py-2 font-mono-code">{log.ip_address}</td>
                <td className="px-3 py-2">{log.is_mock ? <span className="text-amber-500 text-xs">DEMO</span> : '—'}</td>
                <td className="px-3 py-2 text-muted-foreground">{new Date(log.created_at).toLocaleString()}</td>
              </tr>
            ))}
        </tbody></table>
      </div>
    </div>
  );

  // ── N/A Reports ──
  const handleClaimCredits = async () => {
    try {
      const res = await apiCall('POST', '/admin/na-reports/claim', { credits_to_claim: claimAmount });
      setClaimModal({ type: 'success', credits: res.credits_claimed });
      apiCall('GET', '/admin/na-reports').then(setNaReportsData);
    } catch (err) {
      const detail = err.response?.data?.detail || '';
      setClaimModal({ type: 'error', message: detail });
    }
  };

      const renderNaReports = () => {
    const { reports, monthly_count, claimed_so_far, claims_used, can_claim_more, max_claimable_now } = naReportsData;
    const effectiveMax = Math.min(max_claimable_now, 10 - claimed_so_far);
    return (
      <div>
        {/* Header */}
        <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
          <div>
            <h3 className="font-semibold text-base">N/A Reports</h3>
            <p className="text-xs text-muted-foreground mt-0.5">Credit reports where the bureau returned no score</p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            {/* Monthly stats */}
            <div className="px-3 py-2 bg-amber-500/10 border border-amber-500/20 rounded-xl text-center">
              <p className="text-xs text-muted-foreground">This Month</p>
              <p className="text-xl font-bold text-amber-600">{monthly_count}</p>
              <p className="text-xs text-muted-foreground">N/A Reports</p>
            </div>
            <div className="px-3 py-2 bg-secondary border border-border rounded-xl text-center">
              <p className="text-xs text-muted-foreground">Claimed</p>
              <p className="text-xl font-bold text-primary">{claimed_so_far}<span className="text-sm font-normal text-muted-foreground">/10</span></p>
              <p className="text-xs text-muted-foreground">{claims_used}/2 claims used</p>
            </div>
            {/* Claim section */}
            {can_claim_more && effectiveMax > 0 ? (
              <div className="flex items-center gap-2 p-3 bg-card border border-border rounded-xl">
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Credits to claim (max {effectiveMax})</p>
                  <input type="number" min="1" max={effectiveMax}
                    value={claimAmount}
                    onChange={e => setClaimAmount(Math.min(effectiveMax, Math.max(1, parseInt(e.target.value)||1)))}
                    className="form-input w-20 text-sm text-center py-1" />
                </div>
                <button data-testid="claim-credits-btn" onClick={handleClaimCredits}
                  className="btn-primary flex items-center gap-1.5 px-3 py-2 text-sm self-end">
                  <CreditCard size={14} />Claim
                </button>
              </div>
            ) : (
              <div className={`px-3 py-2 rounded-xl text-sm font-medium border ${monthly_count === 0 ? 'bg-secondary text-muted-foreground border-border' : 'bg-secondary text-muted-foreground border-border'}`}>
                {monthly_count === 0 ? 'No N/A reports yet' : claimed_so_far >= 10 ? '10/10 credits claimed' : '2/2 claims used this month'}
              </div>
            )}
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto rounded-xl border border-border">
          <table className="w-full text-xs"><thead><tr className="border-b border-border bg-secondary/30">
            {['User Type', 'Username', 'Subject', 'IP Address', 'Credits Used', 'Timestamp'].map(h => (
              <th key={h} className="text-left px-3 py-2 text-muted-foreground font-medium whitespace-nowrap">{h}</th>
            ))}
          </tr></thead><tbody>
            {reports.length === 0
              ? <tr><td colSpan={6} className="text-center py-8 text-muted-foreground">No N/A reports found</td></tr>
              : reports.map(r => (
                <tr key={r.id} className="border-b border-border/50 hover:bg-secondary/20">
                  <td className="px-3 py-2">
                    {r.created_by_role === 'ngo'
                      ? <span className="px-1.5 py-0.5 bg-purple-500/10 text-purple-700 rounded text-xs font-medium">NGO User</span>
                      : <span className="px-1.5 py-0.5 bg-primary/10 text-primary rounded text-xs font-medium">Admin</span>}
                  </td>
                  <td className="px-3 py-2 font-medium">
                    {r.created_by_role === 'ngo' ? (r.created_by_name || 'NGO') : (user?.name || 'Admin')}
                  </td>
                  <td className="px-3 py-2 text-muted-foreground">{r.subject_name} · {r.subject_mobile}</td>
                  <td className="px-3 py-2 font-mono-code">{r.ip_address || '—'}</td>
                  <td className="px-3 py-2 text-center">
                    <span className="px-1.5 py-0.5 bg-red-500/10 text-red-600 rounded text-xs font-semibold">{r.credits_deducted ?? 1} credit</span>
                  </td>
                  <td className="px-3 py-2 text-muted-foreground whitespace-nowrap">{new Date(r.created_at).toLocaleString()}</td>
                </tr>
              ))}
          </tbody></table>
        </div>

        {/* Claim Modal */}
        {claimModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
            <div className="bg-card border border-border rounded-2xl p-8 max-w-sm w-full shadow-2xl animate-fade-in-up text-center">
              {claimModal.type === 'success' ? (
                <>
                  <div className="w-14 h-14 rounded-full bg-emerald-500/10 flex items-center justify-center mx-auto mb-4">
                    <CreditCard size={26} className="text-emerald-600" />
                  </div>
                  <h3 className="text-lg font-bold text-foreground mb-2">Claim Submitted!</h3>
                  <p className="text-sm text-muted-foreground mb-5">
                    <strong>{claimModal.credits} credits</strong> claim submitted. The API Provider has been notified and will add credits to your wallet shortly.
                  </p>
                </>
              ) : (
                <>
                  <div className="w-14 h-14 rounded-full bg-destructive/10 flex items-center justify-center mx-auto mb-4">
                    <AlertCircle size={26} className="text-destructive" />
                  </div>
                  <h3 className="text-lg font-bold text-foreground mb-2">Claim Request Not Valid</h3>
                  <p className="text-sm text-muted-foreground mb-5">
                    {claimModal.message?.includes('No N/A')
                      ? 'You have no N/A reports this month. Pull at least one credit report that returns no score before claiming.'
                      : 'Maximum claims or credits reached for this month. You can claim up to 10 credits across 2 claims per month.'}
                  </p>
                </>
              )}
              <button onClick={() => setClaimModal(null)} className="btn-primary w-full">Close</button>
            </div>
          </div>
        )}
      </div>
    );
  };
  // ── Purchase History ──
  const renderPurchaseHistory = () => (
    <div>
      <h3 className="font-semibold text-sm mb-4">{t('purchaseHistory')}</h3>
      <div className="overflow-x-auto rounded-xl border border-border">
        <table className="w-full text-xs"><thead><tr className="border-b border-border bg-secondary/30">
          {['Bundle', 'Credits', 'Amount (₹)', 'Payment ID', t('status'), t('timestamp')].map(h => (
            <th key={h} className="text-left px-3 py-2 text-muted-foreground font-medium">{h}</th>
          ))}
        </tr></thead><tbody>
          {purchases.length === 0 ? <tr><td colSpan={6} className="text-center py-8 text-muted-foreground">{t('noData')}</td></tr> :
            purchases.map(p => (
              <tr key={p.id} className="border-b border-border/50">
                <td className="px-3 py-2">Bundle {p.bundle_type}</td>
                <td className="px-3 py-2 font-bold text-primary">{p.credits}</td>
                <td className="px-3 py-2">₹{p.amount_inr?.toLocaleString()}</td>
                <td className="px-3 py-2 font-mono-code text-xs">{p.razorpay_payment_id || '—'}</td>
                <td className="px-3 py-2">
                  <span className={`px-1.5 py-0.5 rounded text-xs ${p.status === 'completed' ? 'bg-emerald-500/10 text-emerald-600' : p.status === 'pending' ? 'bg-amber-500/10 text-amber-600' : 'bg-destructive/10 text-destructive'}`}>
                    {p.status}
                  </span>
                </td>
                <td className="px-3 py-2 text-muted-foreground">{new Date(p.created_at).toLocaleString()}</td>
              </tr>
            ))}
        </tbody></table>
      </div>
    </div>
  );

  const TABS = {
    'overview': renderOverview,
    'purchase': renderPurchase,
    'ngos': renderNGOs,
    'credit-report': () => <CreditReportViewer consentText={settings.checkbox_consent_content} pdfFooterNote={settings.pdf_footer_note} onReportResult={handleReportResult} />,
    'na-reports': renderNaReports,
    'usage-logs': renderUsageLogs,
    'purchase-history': renderPurchaseHistory,
  };

  return (
    <div className="min-h-screen bg-background" data-testid="admin-dashboard">
      <Sidebar role="admin" activeTab={activeTab} setActiveTab={handleSetActiveTab} logoSettings={logoSettings} />
      <main className="lg:pl-64 pt-14 lg:pt-4 lg:pr-4 lg:pb-4">
        <div className="bg-white dark:bg-card lg:rounded-2xl shadow-sm dark:shadow-none min-h-[calc(100vh-2rem)] overflow-hidden flex flex-col">
          <div className="flex items-center justify-between px-6 py-3.5 border-b border-gray-100 dark:border-border bg-white dark:bg-card shrink-0">
            <span className="text-sm font-semibold text-foreground">
              {activeTab.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
            </span>
            <div className="flex items-center gap-2.5">
              <div className="w-7 h-7 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-bold">
                {user?.name?.[0]?.toUpperCase() || 'A'}
              </div>
              <span className="text-sm text-muted-foreground hidden sm:block">{user?.name || 'Admin'}</span>
            </div>
          </div>
          <div className="flex-1 p-6 lg:p-8">
            {(TABS[activeTab] || (() => <div>Coming soon</div>))()}
          </div>
        </div>
      </main>
      {toast && (
        <div className="fixed bottom-4 right-4 z-50 px-4 py-3 bg-white dark:bg-card border border-gray-100 dark:border-border rounded-xl shadow-lg text-sm animate-fade-in-up">
          {toast}
        </div>
      )}
    </div>
  );
}
